/**
 * Operating plan + bets wire contract (spec #45 §6, with #44's accepted
 * delta 3: `lanes[]` carries `owner_domain`).
 *
 * snake_case verbatim from the FastAPI JSON, per house convention.
 *
 * THE EMPTY STATE IS A UNION, NOT A NULL. No approved plan answers `200`
 * with `{status: "none", draft_proposal_id?}` — an honest empty state, not
 * a 404 and never a placeholder metric. `PlanResponse` is therefore a
 * discriminated union on `status`, so a caller cannot read `goal` off an
 * unplanned tenant without tsc objecting.
 */

export interface PlanGoal {
  success_metric: string;
  target: string | number | null;
  horizon_end: string | null;
}

export interface PlanOwnedChannel {
  key: string;
  owner_domain: string;
  enforced: boolean;
}

export interface PlanEnvelope {
  declared_cents: number | null;
  currency: string | null;
  enforced: boolean;
}

export interface PlanLane {
  key: string;
  display_name: string;
  mandate: string | null;
  cadence: string | null;
  lane_status: string;
  blockers: string[];
  envelope: PlanEnvelope;
  /** Accepted delta 3 — the effect rail shows "this agent's lane" and has
   * no other join from a lane to an agent. */
  owner_domain: string | null;
  /** #45 D3 — the `specialist_key.field` names THIS plan set for the lane,
   * so a setup form can tag them agent-proposed / you-approved. */
  plan_set_fields?: string[];
}

export interface ApprovedPlan {
  status: Exclude<string, 'none'>;
  plan_id: string;
  version: number;
  derived_status: string;
  brief_version: number | null;
  goal: PlanGoal;
  owned_channels: PlanOwnedChannel[];
  lanes: PlanLane[];
  cadence: string | null;
  approved_at: string | null;
  approved_by: string | null;
  provisioned_at: string | null;
  bet_ids: string[];
}

/** The honest empty state: a plan has never been approved for this tenant. */
export interface NoPlan {
  status: 'none';
  /** Present when a draft is already waiting in the approvals inbox. */
  draft_proposal_id?: string | null;
}

export type PlanResponse = ApprovedPlan | NoPlan;

export function isApprovedPlan(plan: PlanResponse): plan is ApprovedPlan {
  return plan.status !== 'none';
}

export interface BetEvidence {
  result: string | null;
  sample_size: number | null;
  confidence_after: number | null;
  source: string | null;
  observed_at: string | null;
}

export interface BetBelief {
  id: string;
  statement: string;
  domain: string;
  status: string;
  stored_confidence: number;
  confidence_now: number;
  last_tested_at: string | null;
  retest_by: string | null;
  half_life_days: number | null;
  evidence_count: number;
  latest_evidence: BetEvidence | null;
}

export interface BetHowWeKnow {
  proxy: string | null;
  target: string | number | null;
  latest_reading: string | number | null;
}

export interface BetCommitment {
  owner_domain: string;
  owner_specialist_key: string | null;
  channels: string[];
  envelope: PlanEnvelope;
  deadline_at: string | null;
  how_we_know: BetHowWeKnow;
  /** `null` renders "not yet committed" — the belief-only state is visible,
   * never hidden and never dressed as a commitment (#45's own rule). */
  locked_at: string | null;
}

export interface Bet {
  id: string;
  label: string;
  bet_status: string;
  belief: BetBelief;
  commitment: BetCommitment;
}

export interface BetsResponse {
  bets: Bet[];
}

export interface BriefMeta {
  version: number;
  source: string;
  author_role: string;
  created_at: string;
}

export interface BriefResponse extends BriefMeta {
  document: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Versions (#45 §6) — the plan document BEFORE it is in force.
//
// The draft the interview produces is a version, not a plan: `GET /api/plan`
// still answers `{status: "none"}` until a human approves it. That is the
// whole seam the terminal card renders across.
// ---------------------------------------------------------------------------

export interface PlanVersionSummary {
  version: number;
  status: string;
  created_at: string | null;
  source: string;
  change_summary: string | null;
  approved_by: string | null;
}

export interface PlanVersionDetail {
  version: number;
  status: string;
  source: string;
  change_summary: string | null;
  /**
   * `complete` | `refused` | ... — `refused` means the drafter would not
   * invent numbers it could not read, and the document was filled from the
   * template instead. The card SAYS so; a silently templated plan read as
   * a plan drawn from the tenant's own data would be the exact dishonesty
   * #45 forbids.
   */
  fill_status: string;
  brief_version: number;
  goal: PlanGoal;
  owned_channels: PlanOwnedChannel[];
  lanes: PlanLane[];
  cadence: string;
  created_at: string | null;
  approved_at: string | null;
  approved_by: string | null;
  provisioned_at: string | null;
  diff_from: number | null;
  diff: Record<string, unknown> | null;
}
